import React from 'react';
import { Modal, Select, message } from 'antd';
import { useRequest } from 'ahooks';
import { getProspectTagCandidates } from 'api/prospect';
import { gpGetProspectTagCandidates } from 'api/group';

type Props = {
    open: boolean;
    clientName: string;
    existingTags: string[];
    onCancel: () => void;
    onSubmit: (nextTags: string[]) => Promise<void>;
    isGroupPractice?: boolean;
    identityId?: number;
};

export default function AddClientTagsModal(props: Readonly<Props>) {
    const { open, clientName, existingTags, onCancel, onSubmit, isGroupPractice, identityId } = props;
    const [value, setValue] = React.useState<string[]>([]);
    const [saving, setSaving] = React.useState(false);
    const isEdit = existingTags.length > 0;

    const normalizeCandidate = React.useCallback((t: unknown): string => {
        if (t == null) return '';
        if (typeof t === 'string') return t.trim();
        if (typeof t === 'object') {
            const o = t as { tagName?: unknown; name?: unknown; label?: unknown; value?: unknown };
            const candidate = o.tagName ?? o.name ?? o.label ?? o.value;
            if (typeof candidate === 'string') return candidate.trim();
        }
        return '';
    }, []);

    const { data: candidatesResp } = useRequest(
        () => isGroupPractice && identityId ? gpGetProspectTagCandidates(identityId) : getProspectTagCandidates(),
        {
            refreshDeps: [open, isGroupPractice, identityId],
            ready: open,
        },
    );

    const candidateTags = React.useMemo(() => {
        const raw = (candidatesResp as any)?.data?.data;
        if (!Array.isArray(raw)) return [];
        return raw.map((x: unknown) => normalizeCandidate(x)).filter(Boolean) as string[];
    }, [candidatesResp, normalizeCandidate]);

    React.useEffect(() => {
        if (open) {
            // Prefill existing tags, and keep them non-removable.
            setValue(existingTags);
        }
    }, [open, existingTags]);

    const options = React.useMemo(() => {
        const set = new Set<string>();
        candidateTags.forEach((t) => set.add(t));
        existingTags.forEach((t) => set.add(t));
        return Array.from(set)
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b))
            .map((t) => ({ label: t, value: t }));
    }, [existingTags, candidateTags]);

    const hasNewTags = React.useMemo(() => {
        const existing = new Set(existingTags);
        return value.some((t) => !existing.has(t));
    }, [existingTags, value]);

    const handleOk = async () => {
        const next = Array.from(new Set([...existingTags, ...value.map((v) => v.trim()).filter(Boolean)]));
        if (next.length === existingTags.length) {
            message.info('No new tags to add.');
            onCancel();
            return;
        }
        setSaving(true);
        try {
            await onSubmit(next);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            open={open}
            title={
                <span>
                    {isEdit ? 'Edit' : 'Add new'} tags for <strong>{clientName}</strong>
                </span>
            }
            onCancel={onCancel}
            onOk={handleOk}
            okText={isEdit ? 'Save' : 'Add tags'}
            okButtonProps={{ disabled: !hasNewTags, loading: saving }}
            cancelButtonProps={{ disabled: saving }}
            destroyOnClose
            width={543}
        >
            <div style={{ marginBottom: 6, fontWeight: 500, color: '#374151' }}>
                Tag <span style={{ color: '#EF4444' }}>*</span>
            </div>
            <Select
                mode="tags"
                value={value}
                onChange={(v) => {
                    // Always keep existing tags; allow adding new tags only.
                    const next = Array.from(
                        new Set([
                            ...existingTags,
                            ...(v as string[]).map((x) => (typeof x === 'string' ? x.trim() : '')).filter(Boolean),
                        ]),
                    );
                    setValue(next);
                }}
                style={{ width: '100%' }}
                placeholder="Enter new tags or select from existing ones"
                options={options}
                tokenSeparators={[',']}
            />
        </Modal>
    );
}
